import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Mail, Clock, Scale, CreditCard, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import {
  submitSupportRequest,
  routeSupportCategory,
  SUPPORT_CATEGORIES,
  type SupportCategory,
} from "@/lib/support.functions";

export const Route = createFileRoute("/support")({
  head: () => ({
    meta: [
      { title: "Support & Contact — ForexMind AI" },
      {
        name: "description",
        content:
          "Contact ForexMind AI: general support, billing & refunds, or legal & compliance. Educational market intelligence platform — we do not provide personalized trading advice.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "ForexMind AI" },
      { property: "og:title", content: "Support & Contact — ForexMind AI" },
      {
        property: "og:description",
        content:
          "Contact ForexMind AI: general support, billing & refunds, or legal & compliance. Educational market intelligence platform.",
      },
      { property: "og:url", content: "https://forexmind.ai/support" },
      { property: "og:image", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:secure_url", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Support & Contact — ForexMind AI" },
      {
        name: "twitter:description",
        content:
          "Contact ForexMind AI: general support, billing & refunds, or legal & compliance. Educational market intelligence platform.",
      },
      { name: "twitter:image", content: "https://forexmind.ai/og-image.png" },
    ],
    links: [{ rel: "canonical", href: "https://forexmind.ai/support" }],
  }),
  component: SupportPage,
});

function SupportPage() {
  const submit = useServerFn(submitSupportRequest);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState<SupportCategory>("Account");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const routedTo = routeSupportCategory(category);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await submit({ data: { name, email, category, message } });
      setSent(true);
      toast.success(`Your message has been routed to ${routedTo}.`);
      setName("");
      setEmail("");
      setMessage("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not submit. Try email instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-10">
      <h1 className="font-display text-3xl font-bold tracking-tight">Support &amp; Contact</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Three dedicated inboxes — pick the one that matches your question.
      </p>

      <div className="mt-6 grid gap-3 md:grid-cols-3">
        <ContactCard
          icon={LifeBuoy}
          title="General Support"
          email="support@forexmind.ai"
          blurb="Account help, dashboard questions, technical issues, and product guidance."
        />
        <ContactCard
          icon={CreditCard}
          title="Billing & Refunds"
          email="billing@forexmind.ai"
          blurb="Subscriptions, invoices, failed payments, cancellations, and 7-day refund requests."
        />
        <ContactCard
          icon={Scale}
          title="Legal & Compliance"
          email="legal@forexmind.ai"
          blurb="Risk disclosure, terms, privacy, regulatory questions, or formal notices."
        />
      </div>

      <Card className="mt-4 flex items-start gap-3 p-4">
        <Clock className="mt-0.5 h-4 w-4 text-primary" />
        <div className="text-sm">
          <div className="font-semibold">Support hours</div>
          <p className="text-muted-foreground">Monday–Friday, 9am–6pm UTC</p>
        </div>
      </Card>

      <Card className="mt-4 border-buy/40 bg-buy/5 p-4 text-sm">
        <div className="font-semibold text-foreground">Refund Policy — 7-day money-back guarantee</div>
        <p className="mt-1 text-muted-foreground">
          If you are unsatisfied within 7 days of your first payment, contact{" "}
          <a href="mailto:billing@forexmind.ai" className="underline">billing@forexmind.ai</a>{" "}
          for a full refund. No questions asked.
        </p>
      </Card>

      <Card className="mt-4 flex items-start gap-3 border-warn/40 bg-warn/5 p-4 text-sm">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warn" />
        <p className="leading-snug">
          <span className="font-semibold">Please note:</span> the ForexMind AI support team
          cannot provide personalized trading advice, market commentary, or signal
          explanations. All market intelligence is provided by our AI council for educational
          purposes only.
        </p>
      </Card>

      <Card className="mt-6 p-5">
        <h2 className="font-display text-lg font-semibold">Contact form</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Your message will be routed automatically to the inbox that matches the category you
          pick below.
        </p>

        <form onSubmit={onSubmit} className="mt-4 grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              required
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              maxLength={255}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="category">Category</Label>
            <Select value={category} onValueChange={(v) => setCategory(v as SupportCategory)}>
              <SelectTrigger id="category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORT_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              Routed to <span className="font-medium text-foreground">{routedTo}</span>
            </p>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              required
              minLength={5}
              maxLength={4000}
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-muted-foreground">
              {sent ? "Thanks — we'll be in touch." : ""}
            </p>
            <Button type="submit" disabled={busy}>
              {busy ? "Sending…" : "Submit"}
            </Button>
          </div>
        </form>
      </Card>
    </main>
  );
}

function ContactCard({
  icon: Icon,
  title,
  email,
  blurb,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  email: string;
  blurb: string;
}) {
  return (
    <Card className="flex flex-col gap-2 p-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <div className="text-sm font-semibold">{title}</div>
      </div>
      <p className="text-xs leading-snug text-muted-foreground">{blurb}</p>
      <a
        href={`mailto:${email}`}
        className="mt-auto inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
      >
        <Mail className="h-3 w-3" />
        {email}
      </a>
    </Card>
  );
}
